import { Dayjs } from "dayjs";
import RepositoryBase from "./RepositoryBase";
import { ResponseEnvelope } from "./RepositoryBase";
import WorkDayGetallResponse from "./WorkDayResponses/WorkDayGetallResponse";
import WorkDayGetResponse from "./WorkDayResponses/WorkDayGetResponse";
import WorkDayGetWorkersResponse from "./WorkDayResponses/WorkDayGetWorkersResponse";
import WorkDayWorkerSummaryResponse from "./WorkDayResponses/WorkDayWorkerSummaryResponse";
import WorkDayGetAllTipsTotalResponse from "./WorkDayResponses/WorkDayGetAllTipsTotalResponse";
import WorkDay from "../Types/WorkDay";
import Enumerable from "linq";
import WorkDayAddResponse from "./WorkDayResponses/WorkDayAddResponse";
import WorkDayGetWorkersTipsResponse from "./WorkDayResponses/WorkDayGetWorkersTipsResponse";
import WorkDayWorkerTipResponse from "./WorkDayResponses/WorkDayWorkerTipResponse";
import WorkDayCheckAllCountedResponse from "./WorkDayResponses/WorkDayCheckAllCountedResponse";
import ImportCsvWorkDaysResponse from "./WorkDayResponses/ImportCsvWorkDaysResponse";
import WorkerData from "../Types/WorkerData";
import { Permissions } from "../Types/Enums/Permissions";
import { Functions } from "../Types/Enums/Functions";
import { CountedState } from "../Types/Enums/CountedState";
import WorkPeriod from "../Types/WorkPeriod";
import WorkPeriodResponse from "./WorkDayResponses/WorkPeriodResponse";

export default class WorkDayRepository extends RepositoryBase {
    private static mapWorker(worker: any): WorkerData {
        return new WorkerData(
            worker.id,
            worker.userId ?? "",
            worker.firstName,
            worker.lastName,
            worker.debt ?? 0,
            0,
            Number(worker.permission) as Permissions,
            Number(worker.function) as Functions,
            worker.cashTip ?? 0,
            worker.pinTip ?? 0,
        );
    }

    private static mapWorkPeriods(workPeriods: WorkPeriodResponse[] | undefined): WorkPeriod[] {
        return (workPeriods ?? []).map((x) => new WorkPeriod(
            x.id,
            x.name ?? "",
            x.date,
            x.pinTip ?? 0,
            x.cashTip ?? 0,
            (x.workers ?? []).map((worker) => this.mapWorker(worker)),
        ));
    }

    private static getUniqueWorkersFromPeriods(workPeriods: WorkPeriod[]): WorkerData[] {
        return Enumerable.from(workPeriods)
            .selectMany((x) => x.Workers ?? [])
            .groupBy((x) => x.Guid)
            .select((x) => x.first())
            .toArray();
    }

    private static mapWorkDaySummary(workDay: any): WorkDayGetResponse {
        const workPeriods = this.mapWorkPeriods(workDay.workPeriods as WorkPeriodResponse[]);
        const workers = workDay.workers?.length > 0
            ? workDay.workers.map((worker: any) => this.mapWorker(worker))
            : this.getUniqueWorkersFromPeriods(workPeriods);

        const periodCashTip = workPeriods.reduce((sum, period) => sum + (period.TipCash ?? 0), 0);
        const periodPinTip = workPeriods.reduce((sum, period) => sum + (period.TipPin ?? 0), 0);

        return {
            id: workDay.id,
            date: workDay.date,
            pinTip: workPeriods.length > 0 ? periodPinTip : (workDay.pinTip ?? 0),
            cashTip: workPeriods.length > 0 ? periodCashTip : (workDay.cashTip ?? 0),
            isClosed: workDay.isClosed ?? false,
            countedState: workDay.countedState ?? CountedState.NotCounted,
            countedBy: workDay.countedBy ?? [],
            workers,
            workPeriods,
        };
    }

    private static getRequestWorkPeriods(workDay: WorkDay) {
        let workPeriods = workDay.WorkPeriods ?? [];
        if (workPeriods.length === 0) {
            workPeriods = [new WorkPeriod("", "Work period 1", workDay.Date, workDay.TipPin, workDay.TipCash, workDay.Workers ?? [])];
        } else if (workPeriods.length === 1) {
            const currentPeriod = workPeriods[0];
            workPeriods = [new WorkPeriod(
                currentPeriod.Id,
                currentPeriod.Name,
                currentPeriod.Date || workDay.Date,
                workDay.TipPin,
                workDay.TipCash,
                workDay.Workers ?? currentPeriod.Workers ?? [],
            )];
        }

        return workPeriods.map((x) => ({
            ...(x.Id ? { id: x.Id } : {}),
            name: x.Name,
            date: x.Date,
            cashTip: x.TipCash,
            pinTip: x.TipPin,
            workers: Enumerable.from(x.Workers ?? []).select((worker) => worker.Guid).toArray(),
        }));
    }

    private static getRequestWorkPeriod(workPeriod: WorkPeriod) {
        return {
            name: workPeriod.Name,
            date: workPeriod.Date,
            cashTip: workPeriod.TipCash,
            pinTip: workPeriod.TipPin,
            workers: Enumerable.from(workPeriod.Workers ?? []).select((worker) => worker.Guid).toArray(),
        };
    }

    public static async getWorkedDays(locationId: string, startDate: Dayjs, endDate: Dayjs): Promise<ResponseEnvelope<WorkDayGetallResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<WorkDayGetallResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/workday/getall`, {
                method: 'POST',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
                body: JSON.stringify({
                    startDate: startDate.format("YYYY-MM-DD"),
                    endDate: endDate.format("YYYY-MM-DD"),
                })
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<WorkDayGetallResponse>(
                true,
                {
                    workDays: data.response.map((x: any) => this.mapWorkDaySummary(x))
                }
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async getWorkDayByDate(locationId: string, date: Dayjs): Promise<ResponseEnvelope<WorkDay>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<WorkDay>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/workday`, {
                method: 'POST',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
                body: JSON.stringify({
                    date: date.format("YYYY-MM-DD"),
                })
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();
            if (data.response == null) return new ResponseEnvelope<WorkDay>(true, undefined);
            const responseData = data.response as import("./WorkDayResponses/WorkDayGetByDateResponse").default;

            const workPeriods = this.mapWorkPeriods(responseData.workPeriods as WorkPeriodResponse[]);
            const workers = responseData.workers?.length > 0
                ? responseData.workers.map((x: any) => this.mapWorker(x))
                : Enumerable.from(workPeriods).selectMany((x) => x.Workers).groupBy((x) => x.Guid).select((x) => x.first()).toArray();

            const cashTip = workPeriods.length > 0
                ? Enumerable.from(workPeriods).sum((x) => x.TipCash)
                : (responseData.cashTip ?? 0);
            const pinTip = workPeriods.length > 0
                ? Enumerable.from(workPeriods).sum((x) => x.TipPin)
                : (responseData.pinTip ?? 0);

            return new ResponseEnvelope<WorkDay>(
                true,
                new WorkDay(
                    responseData.id,
                    responseData.date,
                    pinTip,
                    cashTip,
                    responseData.isClosed,
                    responseData.countedState,
                    responseData.countedBy ?? [],
                    workers,
                    workPeriods,
                )
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async addWorkDay(locationId: string, workDay: WorkDay): Promise<ResponseEnvelope<WorkDayAddResponse>> {
        try {    
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<WorkDayAddResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/workday/create`, {
                method: 'POST',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
                body: JSON.stringify({
                    date: workDay.Date,
                    isClosed: workDay.IsClosed,
                    workPeriods: this.getRequestWorkPeriods(workDay),
                })
            })

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<WorkDayAddResponse>(
                true,
                {
                    id: data.id,
                } as WorkDayAddResponse
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async importCsv(locationId: string, csv: string, year: number): Promise<ResponseEnvelope<ImportCsvWorkDaysResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<ImportCsvWorkDaysResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            const response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/workday/import/csv`, {
                method: "POST",
                mode: "cors",
                credentials: "include",
                headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
                body: JSON.stringify({
                    csv,
                    year,
                })
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                return new ResponseEnvelope<ImportCsvWorkDaysResponse>(
                    false,
                    undefined,
                    "CSV import failed",
                    data?.error ?? "The CSV file could not be imported."
                );
            }

            return new ResponseEnvelope<ImportCsvWorkDaysResponse>(
                true,
                {
                    success: data.success,
                    createdWorkers: data.createdWorkers ?? 0,
                    createdWorkDays: data.createdWorkDays ?? 0,
                    updatedWorkDays: data.updatedWorkDays ?? 0,
                    deletedWorkDays: data.deletedWorkDays ?? 0,
                    createdWorkPeriods: data.createdWorkPeriods ?? 0,
                    updatedWorkPeriods: data.updatedWorkPeriods ?? 0,
                    deletedWorkPeriods: data.deletedWorkPeriods ?? 0,
                    error: data.error,
                }
            );
        } catch (err) {
            if (err instanceof SyntaxError) return this.invalidFormat();
            if (err instanceof TypeError) return this.unableToSendRequest();
            console.log(err);
            return this.unexpectedError();
        }
    }

    public static async addWorkPeriod(locationId: string, workDayId: string, workPeriod: WorkPeriod): Promise<ResponseEnvelope<{ id: string }>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<{ id: string }>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            const response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/workday/${workDayId}/workperiod`, {
                method: 'POST',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
                body: JSON.stringify(this.getRequestWorkPeriod(workPeriod))
            });

            if (!response.ok) return this.invalidResponse();

            const data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<{ id: string }>(true, { id: data.id });
        } catch (err) {
            if (err instanceof SyntaxError) return this.invalidFormat();
            if (err instanceof TypeError) return this.unableToSendRequest();
            console.log(err);
            return this.unexpectedError();
        }
    }

    public static async updateWorkPeriod(locationId: string, workDayId: string, workPeriod: WorkPeriod): Promise<ResponseEnvelope<null>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<null>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            const response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/workday/${workDayId}/workperiod/${workPeriod.Id}`, {
                method: 'PUT',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
                body: JSON.stringify(this.getRequestWorkPeriod(workPeriod))
            });

            if (!response.ok) return this.invalidResponse();

            const data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<null>(true);
        } catch (err) {
            if (err instanceof SyntaxError) return this.invalidFormat();
            if (err instanceof TypeError) return this.unableToSendRequest();
            console.log(err);
            return this.unexpectedError();
        }
    }

    public static async deleteWorkPeriod(locationId: string, workDayId: string, workPeriodId: string): Promise<ResponseEnvelope<null>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<null>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            const response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/workday/${workDayId}/workperiod/${workPeriodId}`, {
                method: 'DELETE',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
            });

            if (!response.ok) return this.invalidResponse();

            const data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<null>(true);
        } catch (err) {
            if (err instanceof SyntaxError) return this.invalidFormat();
            if (err instanceof TypeError) return this.unableToSendRequest();
            console.log(err);
            return this.unexpectedError();
        }
    }

    public static async updateWorkDay(locationId: string, workDay: WorkDay): Promise<ResponseEnvelope<null>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<null>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/workday/${workDay.Id}`, {
                method: 'PUT',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
                body: JSON.stringify({
                    date: workDay.Date,
                    isClosed: workDay.IsClosed,
                })
            })
            
            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<null>(
                true
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async deleteWorkDay(locationId: string, workDayId: string): Promise<ResponseEnvelope<null>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<null>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/workday/${workDayId}`, {
                method: 'DELETE',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<null>(
                true
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async getWorkers(locationId: string, id: string): Promise<ResponseEnvelope<WorkDayGetWorkersResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<WorkDayGetWorkersResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/workday/${id}/workers`, {
                method: 'GET',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<WorkDayGetWorkersResponse>(
                true,
                {
                    workers: data.response.map((x: any) => ({
                        id: x.id,
                        firstName: x.firstName,
                        lastName: x.lastName,
                        permission: x.permission,
                        function: x.function
                    } as WorkDayWorkerSummaryResponse))
                }
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async getTotalTip(locationId: string, startDate: Dayjs, endDate: Dayjs): Promise<ResponseEnvelope<WorkDayGetAllTipsTotalResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<WorkDayGetAllTipsTotalResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/workday/tips/total`, {
                method: 'POST',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
                body: JSON.stringify({
                    startDate: startDate.format("YYYY-MM-DD"),
                    endDate: endDate.format("YYYY-MM-DD"),
                })
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<WorkDayGetAllTipsTotalResponse>(
                true,
                {
                    result: data.result
                } as WorkDayGetAllTipsTotalResponse
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async getWorkersTips(locationId: string, startDate: Dayjs, endDate: Dayjs): Promise<ResponseEnvelope<WorkDayGetWorkersTipsResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<WorkDayGetWorkersTipsResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/workday/workers/tips`, {
                method: 'POST',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
                body: JSON.stringify({
                    startDate: startDate.format("YYYY-MM-DD"),
                    endDate: endDate.format("YYYY-MM-DD"),
                })
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<WorkDayGetWorkersTipsResponse>(
                true,
                {
                    result: data.response.map((x: any) => ({
                        id: x.id,
                        firstName: x.firstName,
                        lastName: x.lastName,
                        debt: x.debt,
                        cashTip: x.cashTip,
                        pinTip: x.pinTip
                    } as WorkDayWorkerTipResponse))
                }
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async checkAllCounted(locationId: string, startDate: Dayjs, endDate: Dayjs): Promise<ResponseEnvelope<WorkDayCheckAllCountedResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<WorkDayCheckAllCountedResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/workday/checkcounted`, {
                method: 'POST',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
                body: JSON.stringify({
                    startDate: startDate.format("YYYY-MM-DD"),
                    endDate: endDate.format("YYYY-MM-DD"),
                })
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<WorkDayCheckAllCountedResponse>(
                true,
                {
                    isAllCounted: data.isAllCounted,
                    countedBy: data.countedBy,
                    countedOn: data.countedOn
                }
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async setAllCounted(locationId: string, startDate: Dayjs, endDate: Dayjs, isCounted: boolean): Promise<ResponseEnvelope<null>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope(false, null, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/workday/counted`, {
                method: 'POST',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
                body: JSON.stringify({
                    startDate: startDate.format("YYYY-MM-DD"),
                    endDate: endDate.format("YYYY-MM-DD"),
                    isCounted: isCounted,
                })
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope(true, null);
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }
}
